import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal — scroll-triggered fade + slide-up wrapper.
 *
 * Uses IntersectionObserver once (unobserves after first reveal so we don't
 * re-animate on scroll back up). CSS lives in index.css (`.v2-reveal`,
 * `.is-visible`, `.delay-N`). Respects prefers-reduced-motion via the CSS
 * block — no JS branch needed.
 *
 * Usage:
 *   <Reveal><h2>…</h2></Reveal>
 *   <Reveal delay={2} as="section">…</Reveal>
 */
export default function Reveal({
  children,
  delay = 0,
  as: Tag = 'div',
  className = '',
  threshold = 0.12,
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      // No IO (very old browser) — just reveal immediately.
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  const delayClass = delay ? ` delay-${delay}` : '';
  const visibleClass = visible ? ' is-visible' : '';

  return (
    <Tag ref={ref} className={`v2-reveal${delayClass}${visibleClass} ${className}`} {...rest}>
      {children}
    </Tag>
  );
}
